import React, {FC, useState} from 'react';

import {ArrowDown, CycleMini, Icon} from '@optimaxdev/icons';
import classNames from 'classnames';
import {addDays} from 'date-fns/addDays';
import {format} from 'date-fns/format';
import AnimateHeight from 'react-animate-height';

import {QuoteItemV2} from 'api/magento/magentoAPI.schemas';
import {BaseLensesVistaProductItem} from 'components/baseProductItems/baseLensesVistaProductItem/baseLensesVistaProductItem';
import {ProductName} from 'components/baseProductItems/productName/productName';
import {Price} from 'components/price/price';
import {VISTA_LENSES_PER_BOX} from 'constants/product';

import {CheckoutItemType} from 'desktop/types/checkoutItemsType';

import {LensesCartItemDetails} from '../../lensesCartItemDetails/lensesCartItemDetails';

import style from '../productItem.scss';
import {SupplyText} from '../supplyText/supplyText';

/**
 * Adds 10 days to the current date
 * @returns {string} - next shipment date
 */
const getNextShipmentDate = (): string => format(addDays(Date.now(), 10), 'MMM. d');

/**
 * Returns the plan price
 * @param {number} countOfMonths - count of months
 * @param {string} relatedProductSkuPrice - price
 * @returns {React.ReactElement} - price for the subscription plan
 */
const getPlanPrice = (
    countOfMonths: number,
    relatedProductSkuPrice: string,
): React.ReactElement => <Price>{Number(relatedProductSkuPrice) * countOfMonths * 2}</Price>;

export type PropsType = {
    item: CheckoutItemType;
};

const SupplyComponent = ({item}: {item: CheckoutItemType}) => {
    const countOfSubscriptionMonths = Number(
        item.subscription?.currentSubscriptionPlan.slice(0, -1),
    );
    return (
        item.subscription && (
            <div className={style.infoBlock}>
                <p className={style.autoRefill}>
                    <Icon height={14} icon={CycleMini} strokeWidth={2} width={14} />
                    Auto Refill
                </p>
                <p className={style.autoRefillText}>
                    After 10-day trial, we will auto refill your supply.
                </p>
                <p className={style.simpleRow}>
                    Refill price:{' '}
                    {getPlanPrice(
                        countOfSubscriptionMonths,
                        item.subscription.relatedProductSkuPrice,
                    )}
                </p>
                <p className={style.simpleRow}>Next payment: {getNextShipmentDate()}</p>
                <p className={classNames(style.simpleRow, style.vistaQuantity)}>
                    Qty: {countOfSubscriptionMonths * VISTA_LENSES_PER_BOX * 2} lenses
                </p>
                <SupplyText item={item as unknown as QuoteItemV2} inVista />
            </div>
        )
    );
};
/**
 * Product Contact lenses Vista Plus Item facelift for checkout page
 */
export const ContactLensesVistaProductItem: FC<PropsType> = ({item}) => {
    const [isOpened, setIsOpened] = useState(false);

    return (
        <div className={style.cartItem}>
            <BaseLensesVistaProductItem
                nameComponent={
                    <ProductName
                        id={item.id}
                        isCartPage={false}
                        sku={item.product.sku}
                        windowName={item.windowName}
                        isVista
                    />
                }
                item={item as unknown as QuoteItemV2}
                supplyComponent={<SupplyComponent item={item} />}
            >
                <button
                    className={style.moreDetailsBtn}
                    onClick={() => setIsOpened(!isOpened)}
                    type="button"
                >
                    <span className={style.moreDetailsTitle}>More Details</span>
                    <Icon
                        className={classNames(style.arrow, {
                            [style.iconActive]: isOpened,
                        })}
                        height={6}
                        icon={ArrowDown}
                        strokeWidth={2}
                        width={10}
                    />
                </button>
            </BaseLensesVistaProductItem>
            <AnimateHeight duration={300} height={isOpened ? 'auto' : 0}>
                <LensesCartItemDetails item={item} />
            </AnimateHeight>
        </div>
    );
};
